import type { PlatformPreferences, PreferredVertical } from "@/types";

export const PLATFORM_PREFERENCES_ONBOARDING_VERSION = 1;
export const ANONYMOUS_PLATFORM_PREFERENCES_STORAGE_KEY =
  "nomadic_anonymous_platform_preferences";
const PENDING_USER_PLATFORM_PREFERENCES_STORAGE_KEY_PREFIX =
  "nomadic_pending_user_platform_preferences";

export function getPendingUserPlatformPreferencesStorageKey(
  userId: string,
): string {
  return `${PENDING_USER_PLATFORM_PREFERENCES_STORAGE_KEY_PREFIX}:${encodeURIComponent(userId)}`;
}

export const PREFERRED_VERTICAL_OPTIONS: ReadonlyArray<{
  value: Exclude<PreferredVertical, "general">;
  label: string;
  shortDescription: string;
}> = [
  {
    value: "automotive",
    label: "Automotive",
    shortDescription: "Driving, autonomy, perception, and simulation",
  },
  {
    value: "robotics",
    label: "Robotics",
    shortDescription: "Tasks, manipulation, failures, and recovery",
  },
  {
    value: "egocentric",
    label: "Egocentric",
    shortDescription: "Human activities, procedures, and workflows",
  },
  {
    value: "construction",
    label: "Construction",
    shortDescription: "Equipment, safety, cycles, and operations",
  },
];

const PREFERRED_VERTICALS = new Set<PreferredVertical>([
  ...PREFERRED_VERTICAL_OPTIONS.map((option) => option.value),
  "general",
]);

export function isPreferredVertical(value: unknown): value is PreferredVertical {
  return (
    typeof value === "string" &&
    PREFERRED_VERTICALS.has(value as PreferredVertical)
  );
}

export function normalizePlatformPreferences(
  value: unknown,
): PlatformPreferences | undefined {
  if (!value || typeof value !== "object") return undefined;

  const preferences = value as Record<string, unknown>;
  const preferredVertical = isPreferredVertical(preferences.preferred_vertical)
    ? preferences.preferred_vertical
    : undefined;
  const onboardingVersion =
    typeof preferences.onboarding_version === "number" &&
    Number.isInteger(preferences.onboarding_version)
      ? preferences.onboarding_version
      : 0;
  const onboardingCompletedAt =
    typeof preferences.onboarding_completed_at === "string"
      ? preferences.onboarding_completed_at
      : undefined;

  return {
    preferred_vertical: preferredVertical,
    onboarding_version: onboardingVersion,
    onboarding_completed_at: onboardingCompletedAt,
  };
}

export function hasCompletedPlatformOnboarding(
  preferences?: PlatformPreferences,
): boolean {
  return (
    (preferences?.onboarding_version ?? 0) >=
      PLATFORM_PREFERENCES_ONBOARDING_VERSION &&
    Boolean(preferences?.onboarding_completed_at)
  );
}

export function createCompletedPlatformPreferences(
  preferredVertical: PreferredVertical,
): PlatformPreferences {
  return {
    preferred_vertical: preferredVertical,
    onboarding_version: PLATFORM_PREFERENCES_ONBOARDING_VERSION,
    onboarding_completed_at: new Date().toISOString(),
  };
}

function readStoredPlatformPreferences(
  storageKey: string,
): PlatformPreferences | undefined {
  if (typeof window === "undefined") return undefined;

  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return undefined;

    const preferences = normalizePlatformPreferences(JSON.parse(raw));
    return preferences?.preferred_vertical ? preferences : undefined;
  } catch {
    return undefined;
  }
}

function writeStoredPlatformPreferences(
  storageKey: string,
  preferredVertical: PreferredVertical,
): PlatformPreferences {
  const preferences = createCompletedPlatformPreferences(preferredVertical);

  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(preferences));
    } catch {
      // State still retains the preference for this page when storage is blocked.
    }
  }

  return preferences;
}

function clearStoredPlatformPreferences(storageKey: string): void {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.removeItem(storageKey);
  } catch {
    // The in-memory preference can still be cleared when storage is unavailable.
  }
}

export function readAnonymousPlatformPreferences():
  | PlatformPreferences
  | undefined {
  return readStoredPlatformPreferences(
    ANONYMOUS_PLATFORM_PREFERENCES_STORAGE_KEY,
  );
}

export function writeAnonymousPlatformPreferences(
  preferredVertical: PreferredVertical,
): PlatformPreferences {
  return writeStoredPlatformPreferences(
    ANONYMOUS_PLATFORM_PREFERENCES_STORAGE_KEY,
    preferredVertical,
  );
}

export function clearAnonymousPlatformPreferences(): void {
  clearStoredPlatformPreferences(ANONYMOUS_PLATFORM_PREFERENCES_STORAGE_KEY);
}

export function readPendingUserPlatformPreferences(
  userId: string,
): PlatformPreferences | undefined {
  if (!userId) return undefined;
  return readStoredPlatformPreferences(
    getPendingUserPlatformPreferencesStorageKey(userId),
  );
}

export function writePendingUserPlatformPreferences(
  userId: string,
  preferredVertical: PreferredVertical,
): PlatformPreferences {
  if (!userId) return createCompletedPlatformPreferences(preferredVertical);
  return writeStoredPlatformPreferences(
    getPendingUserPlatformPreferencesStorageKey(userId),
    preferredVertical,
  );
}

export function clearPendingUserPlatformPreferences(userId: string): void {
  if (!userId) return;
  clearStoredPlatformPreferences(
    getPendingUserPlatformPreferencesStorageKey(userId),
  );
}
